import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../../../lib/api';
import { formatCurrency } from '../../../../utils/formatters';
import { usePermesso } from '../../../../context/PermessiContext';
import { ModaleNuovaProforma } from '../../attivo/AccontiPage';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type StatoProformaAcconto = 'DA_INCASSARE' | 'PAGATA' | 'ANNULLATA';
type OrigineProformaAcconto = 'DA_CONVERSIONE_ORDINE' | 'MANUALE_COMMESSA';

interface ProformaItem {
  id: number;
  numero: string;
  origine: OrigineProformaAcconto;
  stato: StatoProformaAcconto;
  commessaId: number;
  ordineId: number | null;
  imponibile: string;
  iva: string;
  totale: string;
  clienteRagioneSociale: string | null;
  dataEmissione: string;
  dataPagamento: string | null;
  totaleIncassato: number;
  residuo: number;
}

interface TabAccontiProps {
  commessaId: number;
  societaId: number;
  commessaNumero: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATO_LABEL: Record<StatoProformaAcconto, string> = {
  DA_INCASSARE: 'Da incassare',
  PAGATA: 'Pagata',
  ANNULLATA: 'Annullata',
};

const STATO_CLS: Record<StatoProformaAcconto, string> = {
  DA_INCASSARE: 'bop-badge-neutral',
  PAGATA: 'bop-badge-success',
  ANNULLATA: 'bop-badge-neutral',
};

const ORIGINE_LABEL: Record<OrigineProformaAcconto, string> = {
  DA_CONVERSIONE_ORDINE: 'Da ordine',
  MANUALE_COMMESSA: 'Manuale',
};

const ORIGINE_CLS: Record<OrigineProformaAcconto, string> = {
  DA_CONVERSIONE_ORDINE: 'bop-badge-info',
  MANUALE_COMMESSA: 'bop-badge-neutral',
};

const fmtDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('it-IT') : '—';

/**
 * TabAcconti — vista filtrata della lista proforme acconto ristretta alla
 * commessa. Consuma GET /api/proforme-acconto con societaId + commessaId
 * pre-applicati. Il click su una riga apre il dettaglio proforma in nuova tab.
 */
export default function TabAcconti({ commessaId, societaId, commessaNumero }: TabAccontiProps) {
  const { can } = usePermesso();
  const canEdit = can('commerciale.acconti', 'edit');

  const [items, setItems] = useState<ProformaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalNuova, setModalNuova] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      // @api GET /api/proforme-acconto?societaId={id}&commessaId={cid} -> {"success":true,"data":[{"id":"number","numero":"string","origine":"string","stato":"string","commessaId":"number","ordineId":"number|null","imponibile":"string","iva":"string","totale":"string","clienteRagioneSociale":"string","dataEmissione":"string","dataPagamento":"string|null","totaleIncassato":"number","residuo":"number"}]}
      const res = await apiFetch<{ success: boolean; data: ProformaItem[] }>(
        `/api/proforme-acconto?societaId=${societaId}&commessaId=${commessaId}`
      );
      setItems(res.data || []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [societaId, commessaId]);

  useEffect(() => { load(); }, [load]);

  const apriDettaglio = (id: number) => {
    window.open(`/commerciale/attivo/acconti/${id}`, '_blank', 'noopener,noreferrer');
  };

  // KPI: esclude le proforme annullate
  const attive = items.filter(p => p.stato !== 'ANNULLATA');
  const totEmesso    = attive.reduce((s, p) => s + Number(p.totale), 0);
  const totIncassato = attive.reduce((s, p) => s + Number(p.totaleIncassato), 0);
  const totResiduo   = attive.reduce((s, p) => s + Number(p.residuo), 0);

  if (loading) {
    return <div className="bop-text-muted bop-tab-preventivi__loading">Caricamento acconti…</div>;
  }
  if (error) {
    return <div className="bop-text-danger bop-tab-preventivi__error">{error}</div>;
  }

  return (
    <div className="bop-tab-preventivi">
      <div className="bop-page-header bop-tab-preventivi__header">
        <h2 className="bop-page-title bop-tab-preventivi__title">Acconti della commessa</h2>
        {canEdit && (
          <button className="bop-btn bop-btn-primary" onClick={() => setModalNuova(true)}>
            + Nuova proforma
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="bop-empty-state bop-tab-preventivi__empty">
          Nessuna proforma acconto su questa commessa.
        </div>
      ) : (
        <>
          <div className="bop-sal__kpi-row">
            <div>
              <span className="bop-sal__kpi-label">Emesso:</span>
              <span className="bop-sal__kpi-value--total">{formatCurrency(totEmesso)}</span>
            </div>
            <div>
              <span className="bop-sal__kpi-label">Incassato:</span>
              <span className="bop-sal__kpi-value--approved">{formatCurrency(totIncassato)}</span>
            </div>
            <div>
              <span className="bop-sal__kpi-label">Residuo:</span>
              <span className="bop-sal__kpi-value--total">{formatCurrency(totResiduo)}</span>
            </div>
            <span className="bop-sal__kpi-count">({items.length} proforme acconto)</span>
          </div>

          <div className="bop-card bop-tab-preventivi__card">
            <table className="bop-table bop-table--clickable bop-tab-preventivi__table">
              <thead>
                <tr>
                  <th>Numero</th>
                  <th>Origine</th>
                  <th>Ordine</th>
                  <th>Cliente</th>
                  <th className="bop-text-right">Totale</th>
                  <th className="bop-text-right">Incassato</th>
                  <th className="bop-text-right">Residuo</th>
                  <th>Stato</th>
                  <th>Data emissione</th>
                </tr>
              </thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.id}
                      className="bop-tab-preventivi__row"
                      onClick={() => apriDettaglio(p.id)}>
                    <td>{p.numero}</td>
                    <td>
                      <span className={`bop-badge ${ORIGINE_CLS[p.origine]}`}>
                        {ORIGINE_LABEL[p.origine]}
                      </span>
                    </td>
                    <td>
                      {p.ordineId
                        ? <span>#{p.ordineId}</span>
                        : <span className="bop-text-muted">—</span>}
                    </td>
                    <td>{p.clienteRagioneSociale || <span className="bop-text-muted">—</span>}</td>
                    <td className="bop-text-right">{formatCurrency(Number(p.totale))}</td>
                    <td className="bop-text-right">{formatCurrency(Number(p.totaleIncassato))}</td>
                    <td className="bop-text-right">{formatCurrency(Number(p.residuo))}</td>
                    <td>
                      <span className={`bop-badge ${STATO_CLS[p.stato]}`}>
                        {STATO_LABEL[p.stato]}
                      </span>
                    </td>
                    <td>{fmtDate(p.dataEmissione)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {modalNuova && (
        <ModaleNuovaProforma
          commessaFissa={{ id: commessaId, numero: commessaNumero, societaId }}
          onClose={() => setModalNuova(false)}
          onCreated={() => { setModalNuova(false); load(); }}
        />
      )}
    </div>
  );
}
